/**
 * Helper de reCAPTCHA v3 — ACHIEVERY-1363 (módulo newsletter "Stay in the loop").
 *
 * reCAPTCHA v3 es invisible: no hay challenge ni checkbox. Corre en background, observa la
 * interacción y, por cada "acción", genera un TOKEN de un solo uso. Ese token viaja en el
 * submit y lo verifica el backend (la Cloudpage SSJS de SFMC) contra
 * `https://www.google.com/recaptcha/api/siteverify` usando la CLAVE SECRETA (que vive solo
 * en el server, nunca en el front). El front solo usa la SITE KEY pública.
 *
 * Doc: https://developers.google.com/recaptcha/docs/v3
 *
 * El render es EXPLÍCITO (`?render=explicit` + `grecaptcha.render`), no automático: es la única
 * forma de sacar el badge de su `position: fixed` y meterlo dentro del layout (`badge: 'inline'`).
 * Quien aporta el contenedor es `RecaptchaBadge.vue`.
 *
 * El script se carga de forma lazy (solo cuando se monta el form) e idempotente (una sola vez).
 */

const RECAPTCHA_SITE_KEY = process.env.VUE_APP_RECAPTCHA_SITE_KEY || '';
const SCRIPT_ID = 'recaptcha-v3';

// Techo para el token: si no responde ni la promesa ni el callback de `execute`, cortamos.
const EXECUTE_TIMEOUT_MS = 10000;

// Cachea la promesa de carga: `loadRecaptcha()` puede llamarse varias veces (mount + submit)
// pero el script se inyecta una sola vez. En error se limpia, así el submit puede reintentar.
let loadPromise = null;

// Idem para el widget: un solo `grecaptcha.render()` por página (repetirlo sobre el mismo nodo
// tira error).
let widgetPromise = null;

// `callback` del widget. Se define una vez en el render y cada `executeRecaptcha` lo reapunta
// al resolve de su propia llamada.
let pendingCallback = null;

// Un widget invisible ya usado necesita reset antes de volver a pedir token.
let alreadyExecuted = false;

/**
 * ¿Hay site key configurada? Sirve para no intentar cargar el script si falta la env var.
 * @returns {boolean}
 */
export function hasRecaptchaKey() {
  return Boolean(RECAPTCHA_SITE_KEY);
}

/**
 * Idioma del badge. El texto "Protegido por reCAPTCHA" y los links de Privacidad/Términos los
 * renderiza Google dentro de su iframe, así que no se pueden traducir desde acá: el único control
 * es el parámetro `hl` del script, que define qué bundle sirve (`recaptcha__es.js`).
 * @param {string} [prismicLang]
 * @returns {string} 'es' | 'en'
 */
function recaptchaLang(prismicLang) {
  return prismicLang === 'es-es' ? 'es' : 'en';
}

/**
 * Inyecta el script de reCAPTCHA (idempotente) y resuelve con el objeto `grecaptcha`.
 * El `hl` queda fijado en esta primera llamada: un cambio de idioma en caliente no lo mueve.
 * @param {string} [prismicLang] - Idioma del badge.
 * @returns {Promise<Object>} grecaptcha
 */
export function loadRecaptcha(prismicLang) {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (!RECAPTCHA_SITE_KEY) {
      reject(new Error('reCAPTCHA: falta VUE_APP_RECAPTCHA_SITE_KEY'));
      return;
    }
    // Ya disponible (cargado por otro medio, p. ej. HMR).
    if (window.grecaptcha && window.grecaptcha.render) {
      resolve(window.grecaptcha);
      return;
    }
    // Ya inyectado pero todavía cargando: engancharse a sus eventos.
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.grecaptcha));
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA: no se pudo cargar el script')));
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit&hl=${recaptchaLang(prismicLang)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => reject(new Error('reCAPTCHA: no se pudo cargar el script'));
    document.head.appendChild(script);
  }).catch((error) => {
    loadPromise = null;
    throw error;
  });

  return loadPromise;
}

/**
 * `grecaptcha.ready` existe en el api.js actual, pero no está documentado para render explícito:
 * si no estuviera, seguimos de largo (el script ya cargó).
 */
function whenReady(grecaptcha, callback) {
  if (typeof grecaptcha.ready === 'function') {
    grecaptcha.ready(callback);
    return;
  }
  callback();
}

/**
 * Contenedor de descarte para el badge, por si nadie aportó uno. Preferimos volver al badge
 * flotante de siempre antes que dejar el form sin token.
 * @returns {HTMLElement}
 */
function floatingHost() {
  const host = document.createElement('div');
  document.body.appendChild(host);
  return host;
}

/**
 * Renderiza el widget (idempotente) y resuelve con su ID.
 * @param {HTMLElement} [container] - Nodo donde va el badge. Sin él, badge flotante.
 * @param {string} [prismicLang] - Idioma del badge.
 * @returns {Promise<number>} widget ID (lo pide `grecaptcha.execute`, NO la site key)
 */
export function renderRecaptcha(container, prismicLang) {
  if (widgetPromise) return widgetPromise;

  widgetPromise = loadRecaptcha(prismicLang).then((grecaptcha) => new Promise((resolve) => {
    whenReady(grecaptcha, () => {
      resolve(grecaptcha.render(container || floatingHost(), {
        sitekey: RECAPTCHA_SITE_KEY,
        size: 'invisible',
        // Badge NATIVO (el colapsado de siempre, con su hover). NO se usa 'inline': ese modo
        // renderiza otro layout interno. Acá el badge se ancla al contenedor por CSS (app.scss).
        badge: 'bottomright',
        callback: (token) => {
          if (pendingCallback) pendingCallback(token);
        }
      }));
    });
  })).catch((error) => {
    widgetPromise = null;
    throw error;
  });

  return widgetPromise;
}

/**
 * Ejecuta reCAPTCHA para una acción y devuelve el token.
 * @param {string} action - Nombre de la acción (verificable del lado del backend). Ej: 'newsletter_subscribe'.
 * @returns {Promise<string>} token de un solo uso a enviar al backend.
 */
export function executeRecaptcha(action = 'submit') {
  return Promise.all([loadRecaptcha(), renderRecaptcha()])
    .then(([grecaptcha, widgetId]) => new Promise((resolve, reject) => {
      let timer = null;
      let settled = false;

      const close = () => {
        settled = true;
        pendingCallback = null;
        clearTimeout(timer);
      };
      const ok = (token) => {
        if (settled || !token) return;
        close();
        resolve(token);
      };
      const fail = (error) => {
        if (settled) return;
        close();
        reject(error);
      };

      timer = setTimeout(() => fail(new Error('reCAPTCHA: no llegó el token')), EXECUTE_TIMEOUT_MS);

      // El token puede llegar por dos vías, según cómo trate Google a esta clave: la promesa que
      // devuelve `execute` (lo documentado para v3) o el `callback` del widget (lo documentado
      // para invisible v2). Escuchamos las dos y gana la primera que responda.
      pendingCallback = ok;

      // Un token por uso: si ya ejecutamos, hay que resetear el widget antes de pedir otro.
      if (alreadyExecuted) {
        try { grecaptcha.reset(widgetId); } catch (e) { /* v3 puede no necesitarlo */ }
      }
      alreadyExecuted = true;

      const pending = grecaptcha.execute(widgetId, { action });
      if (pending && typeof pending.then === 'function') {
        pending.then(ok, fail);
      }
    }));
}
